import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { LeaderService } from '../../services/leader.service';
import { NewMeetingSchedulerComponent } from './new-meeting-scheduler/new-meeting-scheduler.component';
import moment from 'moment';
import { Editor, Toolbar } from 'ngx-editor';
import { MsalService } from '@azure/msal-angular';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthenticationResult } from '@azure/msal-common/lib/types/response/AuthenticationResult';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthManager } from 'src/app/shared/auth/AuthManager';

@Component({
  selector: 'app-meeting-detail-dialog',
  templateUrl: './meeting-detail-dialog.component.html',
  styleUrls: ['./meeting-detail-dialog.component.scss'],
  standalone: false,
})
export class MeetingDetailDialogComponent implements OnInit, OnDestroy {
  newCommentText: string ='';
  meetingComments: any;
  currentUserName: string = '';
  isDeleting = false;
  showCancelConfirmDialog = false;
  editor: Editor = new Editor();
  editEditor: Editor = new Editor();
  editingComment: any = null;
  editText: string = '';
  deleteConfirmComment: any = null;
  loading!: boolean;
  result: any;
  isSignedIn!: boolean;

  // Meeting assets state
  transcriptStatus: any = null;
  transcripts: any[] = [];
  recordings: any[] = [];
  aiNotes: any = null;
  isLoadingAssets = false;
  isFetching = false;
  isDownloadingTranscript = false;
   isDownloadingRecording = false;
   isDownloadingAiNotes = false;
   showAiNotesViewer = false;
   isLoadingRecording = false;
  showTranscriptViewer = false;
  selectedTranscript: any = null;
  showRecordingPlayer = false;
  recordingPlayUrl: SafeResourceUrl | null = null;
  recordingBlobUrl: string | null = null;
  assetsLoadError = false;

// Recurring meeting occurrence state
isRecurring = false;
occurrences: any[] = [];
selectedOccurrenceDate: string = '';
selectedOccurrenceCacheId: number | null = null;
isLoadingOccurrences = false;
occurrenceAssetsLoaded = false;
selectedOccurrence: any = null;

// Occurrence time editor
showOccurrenceTimeEditor = false;
occurrenceEditStart: string = '';
occurrenceEditEnd: string = '';
savingOccurrenceTime = false;

// Occurrence cancel confirmation
showCancelOccurrenceConfirm = false;
cancellingOccurrence = false;

  // Safe style for HTML rendering
  safeHtml(content: string) {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  constructor(
    public dialogRef: MatDialogRef<MeetingDetailDialogComponent>, public leaderService: LeaderService,
    @Inject(MAT_DIALOG_DATA) public data: any, private msal: MsalService, private http: HttpClient,
    private sanitizer: DomSanitizer, private dialog: MatDialog, private authMgr: AuthManager
  ) { }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.getCommentsByMeetingId();
    this.isRecurring = this.data?.isRecurring === true || this.data?.isRecurring === 1;
    if (this.isRecurring) {
      this.loadOccurrences();
    } else {
      this.loadMeetingAssets();
    }
  }

  loadCurrentUser(): void {
    this.authMgr.getCurrentUser().subscribe((user: any) => {
      this.currentUserName = user?.displayName || user?.name || '';
    });
  }

    toolbar: Toolbar = [
      ['bold', 'italic'],
      ['underline', 'strike'],
      ['code', 'blockquote'],
      ['ordered_list', 'bullet_list'],
      [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
      ['text_color', 'background_color'],
      ['align_left', 'align_center', 'align_right', 'align_justify'],
    ];

  ngOnDestroy(): void {
    this.editor.destroy();
    this.editEditor.destroy();
  }

  joinMeeting(url: string): void {
    window.open(url, '_blank');
  }

  // ---- Meeting Assets Methods ----

  loadMeetingAssets(): void {
    if (!this.data?.meetingId) return;
    this.isLoadingAssets = true;
    this.assetsLoadError = false;

    this.leaderService.getMeetingTranscriptStatus(this.data.meetingId).subscribe({
      next: (status: any) => {
        this.transcriptStatus = status?.data || status;
        this.fetchDetailsIfAvailable();
      },
      error: () => {
        this.assetsLoadError = true;
        this.isLoadingAssets = false;
      }
    });
  }

  private fetchDetailsIfAvailable(): void {
    const status = this.transcriptStatus;
    const meetingId = this.data.meetingId;

    // Fetch transcripts if available
    if (status?.hasTranscripts || status?.transcriptAvailable) {
      this.leaderService.getTranscripts(meetingId).subscribe({
        next: (res: any) => {
          this.transcripts = Array.isArray(res) ? res : (res?.data || []);
        }
      });
    }

    // Fetch recordings if available
    if (status?.hasRecordings || status?.recordingAvailable) {
      this.leaderService.getRecordings(meetingId).subscribe({
        next: (res: any) => {
          this.recordings = Array.isArray(res) ? res : (res?.data || []);
        }
      });
    }

    // Auto-fetch AI notes from Copilot API
    this.leaderService.fetchAiNotes(meetingId).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        const notes = Array.isArray(data) ? data : (data?.aiNoteId ? [data] : []);
        if (notes.length > 0) {
          this.aiNotes = notes[0];
        }
      }
    });

    this.isLoadingAssets = false;
  }

  // ---- Recurring Meeting Occurrence Methods ----

  loadOccurrences(preserveDate?: string): void {
    if (!this.data?.meetingId) return;
    this.isLoadingOccurrences = true;

    const now = new Date();
    const rangeStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
    const rangeEnd = new Date(now.getFullYear() + 1, 0, 31).toISOString();

    this.leaderService.getSeriesOccurrences(this.data.meetingId, rangeStart, rangeEnd).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        this.occurrences = (Array.isArray(data) ? data : []).filter((o: any) => !o.isCancelled);
        this.isLoadingOccurrences = false;

        if (this.occurrences.length > 0) {
          const today = new Date().toISOString().split('T')[0];
          let target: any | undefined;

          if (preserveDate) {
            target = this.occurrences.find((o: any) => o.occurrenceDate === preserveDate);
          }

          if (!target) {
            const upcoming = this.occurrences.find((o: any) => o.occurrenceDate >= today);
            target = upcoming || this.occurrences[0];
          }

          if (target) {
            this.onOccurrenceDateChange(target.occurrenceDate);
          }
        }
      },
      error: () => {
        this.isLoadingOccurrences = false;
        this.assetsLoadError = true;
      }
    });
  }

  onOccurrenceDateChange(dateStr: string): void {
    if (!dateStr || !this.data?.meetingId) return;

    this.selectedOccurrenceDate = dateStr;
    this.selectedOccurrence = this.occurrences.find((o: any) => o.occurrenceDate === dateStr) || null;
    this.isLoadingAssets = true;
    this.occurrenceAssetsLoaded = false;
    this.transcripts = [];
    this.recordings = [];
    this.aiNotes = null;

    // Get or create cache row for this occurrence
    this.leaderService.getOccurrenceCache(this.data.meetingId, dateStr).subscribe({
      next: (res: any) => {
        const cache = res?.data || res;
        if (cache?.cacheId) {
          this.selectedOccurrenceCacheId = cache.cacheId;
          this.loadOccurrenceAssets(cache.cacheId);
        } else {
          this.isLoadingAssets = false;
        }
      },
      error: () => {
        this.isLoadingAssets = false;
        this.assetsLoadError = true;
      }
    });
  }

  private loadOccurrenceAssets(cacheId: number): void {
    this.leaderService.getOccurrenceAssets(cacheId).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        this.transcripts = data?.transcripts || [];
        this.recordings = data?.recordings || [];
        const aiNotesList = data?.aiNotes || [];
        this.aiNotes = aiNotesList.length > 0 ? aiNotesList[0] : null;
        this.transcriptStatus = data?.status || null;
        this.isLoadingAssets = false;
        this.occurrenceAssetsLoaded = true;
      },
      error: () => {
        this.isLoadingAssets = false;
        this.assetsLoadError = true;
      }
    });
  }

  triggerOccurrenceFetch(): void {
    if (!this.selectedOccurrenceCacheId) return;
    this.isFetching = true;

    this.leaderService.triggerOccurrenceFetch(this.selectedOccurrenceCacheId).subscribe({
      next: () => {
        this.loadOccurrenceAssets(this.selectedOccurrenceCacheId!);
        this.isFetching = false;
      },
      error: () => {
        this.isFetching = false;
      }
    });
  }

  // ---- Single-Occurrence Edit / Delete Methods ----

  formatOccTime(timeStr: string | null): string {
    if (!timeStr) return '';
    const trimmed = timeStr.trim();
    if (trimmed.length >= 19) return trimmed.substring(11, 16);
    if (trimmed.length >= 8) return trimmed.substring(0, 5);
    return trimmed;
  }

  formatDateOnly(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length < 3) return dateStr;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2].substring(0, 2), 10);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return dateStr;
    return new Date(year, month, day).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  }

  occurrenceTimeZoneCode(tz?: string | null): string {
    const map: Record<string, string> = {
      'Eastern Standard Time': 'ET',
      'Central Standard Time': 'CT',
      'Mountain Standard Time': 'MT',
      'Pacific Standard Time': 'PT',
      'Hawaiian Standard Time': 'HST'
    };
    return tz ? (map[tz] || tz) : '';
  }

  occurrenceTimeZoneName(tz?: string | null): string {
    if (!tz) return '';
    const map: Record<string, string> = {
      'Eastern Standard Time': 'Eastern Time (ET)',
      'Central Standard Time': 'Central Time (CT)',
      'Mountain Standard Time': 'Mountain Time (MT)',
      'Pacific Standard Time': 'Pacific Time (PT)',
      'Hawaiian Standard Time': 'Hawaii Time (HST)'
    };
    return map[tz] || tz;
  }

  occurrenceOptionLabel(occ: any): string {
    if (!occ) return '';
    let label = this.formatDateOnly(occ.occurrenceDate);
    if (occ.startTime) {
      label += ' — ' + this.formatOccTime(occ.startTime);
      const tz = this.occurrenceTimeZoneCode(occ.timeZone);
      if (tz) label += ' (' + tz + ')';
    }
    if (occ.isCancelled) label += ' (Cancelled)';
    return label;
  }

  selectedOccurrenceIsCancelled(): boolean {
    return !!(this.selectedOccurrence && this.selectedOccurrence.isCancelled);
  }

  selectedOccurrenceInPast(): boolean {
    const occ = this.selectedOccurrence;
    if (!occ?.occurrenceDate || !occ?.startTime) return false;
    const start = this.formatOccTime(occ.startTime);
    const end = occ.endTime ? this.formatOccTime(occ.endTime) : start;
    const meetingEnd = moment(`${occ.occurrenceDate} ${end}`, 'YYYY-MM-DD HH:mm');
    return moment().isAfter(meetingEnd);
  }

  canManageSelectedOccurrence(): boolean {
    return !!this.selectedOccurrenceDate &&
           !!this.selectedOccurrence &&
           !this.selectedOccurrenceIsCancelled() &&
           !this.selectedOccurrenceInPast();
  }

  openOccurrenceTimeEditor(): void {
    const occ = this.selectedOccurrence;
    this.occurrenceEditStart = this.formatOccTime(occ?.startTime) || '09:00';
    this.occurrenceEditEnd = this.formatOccTime(occ?.endTime) || '10:00';
    this.showOccurrenceTimeEditor = true;
  }

  saveOccurrenceTime(): void {
    if (!this.selectedOccurrenceDate || !this.occurrenceEditStart || !this.occurrenceEditEnd) {
      this.leaderService.showNotification('Select both start and end time.', true);
      return;
    }

    this.savingOccurrenceTime = true;
    const meetingId = this.data.meetingId;
    const date = this.selectedOccurrenceDate;

    this.leaderService.updateOccurrenceTime(meetingId, date, this.occurrenceEditStart, this.occurrenceEditEnd).subscribe({
      next: (res: any) => {
        this.savingOccurrenceTime = false;
        const meta = res?.metaData || res?.MetaData || res;
        const details = meta?.resultDetails || [];
        const code = details[0]?.code || details[0]?.status;
        if (code === 'R00' || code === 'SUCCESS') {
          this.leaderService.showNotification('Occurrence time updated successfully!', false);
          this.showOccurrenceTimeEditor = false;
          this.loadOccurrences(date);
        } else {
          const msg = details[0]?.description || details[0]?.message || 'Failed to update occurrence time.';
          this.leaderService.showNotification(msg, true);
        }
      },
      error: () => {
        this.savingOccurrenceTime = false;
        this.leaderService.showNotification('Error updating occurrence time. Please try again.', true);
      }
    });
  }

  openCancelOccurrenceConfirm(): void {
    this.showCancelOccurrenceConfirm = true;
  }

  cancelOccurrenceAction(): void {
    if (!this.selectedOccurrenceDate) return;

    this.cancellingOccurrence = true;
    const meetingId = this.data.meetingId;
    const date = this.selectedOccurrenceDate;

    this.leaderService.cancelOccurrence(meetingId, date).subscribe({
      next: (res: any) => {
        this.cancellingOccurrence = false;
        this.showCancelOccurrenceConfirm = false;
        const meta = res?.metaData || res?.MetaData || res;
        const details = meta?.resultDetails || [];
        const code = details[0]?.code || details[0]?.status;
        if (code === 'R00' || code === 'SUCCESS') {
          this.leaderService.showNotification('Occurrence cancelled successfully!', false);
          this.loadOccurrences(date);
        } else {
          const msg = details[0]?.description || details[0]?.message || 'Failed to cancel occurrence.';
          this.leaderService.showNotification(msg, true);
        }
      },
      error: () => {
        this.cancellingOccurrence = false;
        this.showCancelOccurrenceConfirm = false;
        this.leaderService.showNotification('Error cancelling occurrence. Please try again.', true);
      }
    });
  }

  isPastMeeting(): boolean {
    const date = this.data?.scheduledDate;
    const end = this.data?.endTime;
    if (date && end) {
      const meetingEnd = moment(`${date} ${end}`, 'YYYY-MM-DD HH:mm');
      return moment().isAfter(meetingEnd);
    }
    return false;
  }

  canFetchAfterMeeting(): boolean {
    const meetingEndStr = this.data?.meetingEndedAt;
    if (meetingEndStr) {
      const endMoment = moment.utc(meetingEndStr);
      return moment().diff(endMoment) >= 30 * 60 * 1000;
    }

    // Fallback: use scheduledDate + endTime
    const date = this.data?.scheduledDate;
    const end = this.data?.endTime;
    if (date && end) {
      const meetingEnd = moment(`${date} ${end}`, 'YYYY-MM-DD HH:mm');
      return moment().diff(meetingEnd) >= 30 * 60 * 1000;
    }
    return false;
  }

  triggerFetch(): void {
    if (this.isFetching || !this.data?.meetingId) return;
    this.isFetching = true;

    this.leaderService.triggerTranscriptFetch(this.data.meetingId).subscribe({
      next: (res: any) => {
        this.isFetching = false;
        const data = res?.data || res;
        const fetched = data?.transcriptsFetched || 0;
        const recordings = data?.recordingsFetched || 0;
        this.leaderService.showNotification(
          `Fetch complete: ${fetched} transcript(s), ${recordings} recording(s) found.`, false
        );
        this.loadMeetingAssets();
      },
      error: () => {
        this.isFetching = false;
        this.leaderService.showNotification('Failed to trigger fetch. Please try again.', true);
      }
    });
  }

  viewAiNotesInline(): void {
    this.showAiNotesViewer = true;
    this.showTranscriptViewer = false;
    this.showRecordingPlayer = false;
  }

  closeAiNotesViewer(): void {
    this.showAiNotesViewer = false;
  }

  downloadAiNotes(): void {
    if (this.isDownloadingAiNotes || !this.aiNotes) return;
    this.isDownloadingAiNotes = true;

    let content = '';
    if (this.aiNotes.summary) {
      content += '=== SUMMARY ===\n\n' + this.aiNotes.summary + '\n\n';
    }
    if (this.aiNotes.actionItems) {
      content += '=== ACTION ITEMS ===\n\n' + this.aiNotes.actionItems + '\n\n';
    }
    if (!content) {
      content = 'No AI notes content available.';
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai_notes_${this.data.meetingId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.isDownloadingAiNotes = false;
  }

  downloadTranscriptContent(transcript: any): void {
    if (this.isDownloadingTranscript) return;
    this.isDownloadingTranscript = true;
    const raw = transcript.plainText || transcript.content || '';
    const blocks = this.parseWebVtt(raw);
    const readable = blocks.map(b => {
      const header = b.speaker ? `${b.speaker}  ${b.start}` : b.start;
      return `${header}\n${b.text}\n`;
    }).join('\n');
    const blob = new Blob([readable], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${transcript.transcriptId || this.data.meetingId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.isDownloadingTranscript = false;
  }

  downloadRecordingFile(recording: any): void {
    const recordingId = recording.recordingId;
    if (!recordingId || this.isDownloadingRecording) return;
    this.isDownloadingRecording = true;

    this.leaderService.downloadRecording(recordingId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recording_${recordingId}.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isDownloadingRecording = false;
      },
      error: () => {
        this.isDownloadingRecording = false;
        this.leaderService.showNotification('Failed to download recording.', true);
      }
    });
  }

  parsedTranscriptBlocks: { start: string; speaker: string; text: string }[] = [];

  formatTimestamp(ts: string): string {
    const parts = ts.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const secs = parseFloat(parts[2]);
    const sec = Math.floor(secs);
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }
    return `${minutes}:${String(sec).padStart(2, '0')}`;
  }

  parseWebVtt(raw: string): { start: string; speaker: string; text: string }[] {
    const blocks: { start: string; speaker: string; text: string }[] = [];
    const lines = raw.split(/\r?\n/);
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line || line === 'WEBVTT') {
        i++;
        continue;
      }

      const timestampMatch = line.match(
        /(\d{2}:\d{2}:\d{2}\.\d{3})\s*-->\s*\d{2}:\d{2}:\d{2}\.\d{3}/
      );
      if (timestampMatch) {
        const start = timestampMatch[1];
        i++;
        const textLines: string[] = [];
        while (i < lines.length && lines[i].trim() !== '' && !lines[i].includes(' --> ')) {
          textLines.push(lines[i].trim());
          i++;
        }
        const fullText = textLines.join(' ');

        let speaker = '';
        let text = fullText;

        const vTagMatch = fullText.match(/<v([^>]*)>(.*?)<\/v>/);
        if (vTagMatch) {
          speaker = vTagMatch[1].trim();
          text = vTagMatch[2].trim();
        } else {
          const colonIdx = fullText.indexOf(':');
          if (colonIdx > 0 && colonIdx < 60) {
            const potentialSpeaker = fullText.substring(0, colonIdx).trim();
            if (/^[A-Za-z\s.]+$/.test(potentialSpeaker)) {
              speaker = potentialSpeaker;
              text = fullText.substring(colonIdx + 1).trim();
            }
          }
        }

        blocks.push({ start: this.formatTimestamp(start), speaker, text });
      } else {
        i++;
      }
    }
    return blocks;
  }

  viewTranscriptInline(transcript: any): void {
    this.selectedTranscript = transcript;
    const content = transcript.plainText || transcript.content || '';
    this.parsedTranscriptBlocks = this.parseWebVtt(content);
    this.showTranscriptViewer = true;
    this.showRecordingPlayer = false;
  }

  closeTranscriptViewer(): void {
    this.showTranscriptViewer = false;
    this.selectedTranscript = null;
  }

  viewRecordingInApp(recording: any): void {
    const recordingId = recording.recordingId;
    if (!recordingId || this.isLoadingRecording) return;
    this.isLoadingRecording = true;

    this.leaderService.downloadRecording(recordingId).subscribe({
      next: (blob: Blob) => {
        if (this.recordingBlobUrl) {
          window.URL.revokeObjectURL(this.recordingBlobUrl);
        }
        this.recordingBlobUrl = window.URL.createObjectURL(blob);
        this.recordingPlayUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.recordingBlobUrl);
        this.isLoadingRecording = false;
        this.showRecordingPlayer = true;
        this.showTranscriptViewer = false;
      },
      error: () => {
        this.isLoadingRecording = false;
        this.leaderService.showNotification('Failed to load recording.', true);
      }
    });
  }

  closeRecordingPlayer(): void {
    this.showRecordingPlayer = false;
    this.recordingPlayUrl = null;
    if (this.recordingBlobUrl) {
      window.URL.revokeObjectURL(this.recordingBlobUrl);
      this.recordingBlobUrl = null;
    }
  }

  viewInTeams(): void {
    if (this.data?.teamsMeetingId) {
      window.open(
        `https://teams.microsoft.com/l/meeting/recap?meetingId=${encodeURIComponent(this.data.teamsMeetingId)}`,
        '_blank'
      );
    } else if (this.data?.joinUrl) {
      window.open(this.data.joinUrl, '_blank');
    }
  }

  // ---- End Meeting Assets Methods ----

  openEditMeetingDialog(): void {
    this.leaderService.getHospitalList().subscribe((hospitals: any) => {
      const hospList = hospitals?.data || hospitals || [];
      const dialogRef = this.dialog.open(NewMeetingSchedulerComponent, {
        width: '850px',
        maxWidth: '95vw',
        panelClass: 'custom-dialog-container',
        data: { hospitals: hospList, meetingData: this.data },
        disableClose: false
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result?.status === 'updated' || result?.refreshMeetings === true) {
          this.dialogRef.close({ status: 'updated', refreshMeetings: true });
        }
      });
    });
  }

  openCancelConfirmDialog(): void {
    if (!this.data.meetingId) {
      this.leaderService.showNotification('Unable to cancel meeting: Meeting ID not found.', true);
      return;
    }
    this.showCancelConfirmDialog = true;
  }

  confirmCancelMeeting(): void {
    this.showCancelConfirmDialog = false;
    this.isDeleting = true;

    if (this.data.isRecurring) {
      this.leaderService.deleteSeries(this.data.meetingId, true).subscribe({
        next: (res: any) => {
          this.isDeleting = false;
          const meta = res?.resultDetails || res?.MetaData?.resultDetails;
          const code = meta?.[0]?.code || meta?.[0]?.status || res?.resultDetails?.[0]?.code;
          if (code === 'R00' || code === 'SUCCESS') {
            this.leaderService.showNotification('Recurring meeting series cancelled successfully!', false);
            this.dialogRef.close({ status: 'deleted', refreshMeetings: true });
          } else {
            this.leaderService.showNotification('Failed to cancel meeting series. Please try again.', true);
          }
        },
        error: (err) => {
          this.isDeleting = false;
          console.error('Error cancelling meeting series:', err);
          this.leaderService.showNotification('Error cancelling meeting series. Please try again.', true);
        }
      });
    } else {
      this.leaderService.deleteMeeting(this.data.meetingId).subscribe({
        next: (res: any) => {
          this.isDeleting = false;
          if (res?.resultDetails?.[0]?.status === 'SUCCESS' || res?.status === 'SUCCESS') {
            this.leaderService.showNotification('Meeting cancelled successfully!', false);
            this.dialogRef.close({ status: 'deleted', refreshMeetings: true });
          } else {
            this.leaderService.showNotification('Failed to cancel meeting. Please try again.', true);
          }
        },
        error: (err) => {
          this.isDeleting = false;
          console.error('Error cancelling meeting:', err);
          this.leaderService.showNotification('Error cancelling meeting. Please try again.', true);
        }
      });
    }
  }

  rejectCancelMeeting(): void {
    this.showCancelConfirmDialog = false;
  }

  getRecurrenceLabel(pattern: string): string {
    if (this.data?.recurrenceFrequency) {
      return this.getGranularRecurrenceLabel();
    }
    const labels: Record<string, string> = {
      'weekly': 'Weekly',
      'biweekly': 'Bi-weekly',
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'annually': 'Annually'
    };
    return labels[pattern] || pattern;
  }

  private getGranularRecurrenceLabel(): string {
    const freq = this.data.recurrenceFrequency;
    const interval = this.data.recurrenceInterval || 1;
    const daysOfWeek = this.data.recurrenceDaysOfWeek || '';
    const freqLabels: Record<string, string> = {
      'daily': 'day', 'weekly': 'week', 'monthly': 'month', 'yearly': 'year'
    };
    const freqLabel = freqLabels[freq] || freq;
    const intervalStr = interval === 1 ? '' : `${interval} `;
    let result = `Every ${intervalStr}${freqLabel}${interval === 1 ? '' : 's'}`;

    if (freq === 'weekly' && daysOfWeek) {
      const dayLabels: Record<string, string> = {
        'Sun': 'Sunday', 'Mon': 'Monday', 'Tue': 'Tuesday', 'Wed': 'Wednesday',
        'Thu': 'Thursday', 'Fri': 'Friday', 'Sat': 'Saturday'
      };
      const days = daysOfWeek.split(',').map((d: string) => dayLabels[d.trim()] || d.trim());
      result += ` on ${days.join(', ')}`;
    }

    if (this.data.recurrenceEndDate) {
      result += ` until ${new Date(this.data.recurrenceEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }

    return result;
  }


  postCommentByMeetingId() {
    
    this.leaderService.createnotesbymeeting({
      meetingId: this.data.meetingId,
      notesData: this.newCommentText
    }).subscribe((res: any) => {
      if (res.resultDetails[0].status === "SUCCESS") {
        this.leaderService.showNotification('Note added successfully', false);
        this.newCommentText = '';
        this.getCommentsByMeetingId();
      }
      else {
        this.leaderService.showNotification('Failed to add note', true);
        this.newCommentText = '';
        this.getCommentsByMeetingId();
      }

    });
  }

  getCommentsByMeetingId() {
    this.leaderService.getnotesbymeetingid(this.data.meetingId).subscribe((res: any) => {
      this.meetingComments = res.data;
    });
  }

  isOwnComment(comment: any): boolean {
    const name = comment.createdBy || '';
    return name.toLowerCase() === this.currentUserName.toLowerCase();
  }

  startEditComment(comment: any) {
    this.editingComment = comment;
    this.editText = comment.notesData || '';
  }

  cancelEditComment() {
    this.editingComment = null;
    this.editText = '';
  }

  saveEditComment() {
    if (!this.editText.trim()) return;
    const data = {
      notesId: this.editingComment.notesId,
      meetingId: this.data.meetingId,
      notesData: this.editText
    };
    this.leaderService.updatenotesbymeeting(data).subscribe({
      next: (res: any) => {
        this.leaderService.showNotification('Note updated successfully!', false);
        this.editingComment = null;
        this.editText = '';
        this.getCommentsByMeetingId();
      },
      error: () => {
        this.leaderService.showNotification('Failed to update note', true);
      }
    });
  }

  confirmDeleteComment(comment: any) {
    this.deleteConfirmComment = comment;
  }

  cancelDeleteComment() {
    this.deleteConfirmComment = null;
  }

  deleteComment() {
    if (!this.deleteConfirmComment) return;
    const data = {
      notesId: this.deleteConfirmComment.notesId,
      meetingId: this.data.meetingId
    };
    this.leaderService.deletenotesbymeeting(data).subscribe({
      next: (res: any) => {
        this.leaderService.showNotification('Note deleted successfully!', false);
        this.deleteConfirmComment = null;
        this.getCommentsByMeetingId();
      },
      error: () => {
        this.leaderService.showNotification('Failed to delete note', true);
      }
    });
  }

    formatActivityDate(actionDate: any): string {
      if (!actionDate) {
        return '—';
      }
      try {
         const date = moment.utc(actionDate).local();
          return date.format('MMM DD, YYYY hh:mm a');
      } catch (error) {
        console.error('Error formatting date:', error);
        return actionDate;
      }   
    }


    async cancelMeeting(isTeamsMeeting: boolean = false, cancelMessage: string = 'Meeting has been cancelled.'): Promise<void> {
    await this.msal.instance.initialize();
    return new Promise((resolve, reject) => {
      const account = this.msal.instance.getActiveAccount()
      if (!account) {
        console.error('No active account found.')
        reject(new Error('No active account found'))
        return
      }

      const eventId = this.data?.outlookEventId || this.result?.id || this.data?.eventId || this.data?.teamsEventId
      const hasJoinUrl = this.data?.joinUrl
      
      if (!eventId || !hasJoinUrl) {
        console.warn('No calendar event to cancel (missing event ID or join URL). Proceeding with database deletion.')
        resolve()
        return
      }

      this.loading = true

      const scopes = isTeamsMeeting 
        ? ['Calendars.ReadWrite', 'OnlineMeetings.ReadWrite']
        : ['Calendars.ReadWrite']

      this.msal
        .acquireTokenSilent({
          account,
          scopes: scopes,
        })
        .subscribe({
          next: (tokenResponse) => {
            const accessToken = tokenResponse.accessToken

            const body = {
              comment: cancelMessage
            }

            const cancelUrl = `https://graph.microsoft.com/v1.0/me/events/${eventId}/cancel`

            console.log(`${isTeamsMeeting ? 'Teams' : 'Outlook'} Meeting Cancel - URL: ${cancelUrl}, EventID: ${eventId}`)

            this.http
              .post(cancelUrl, body, {
                headers: new HttpHeaders({
                  Authorization: `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                }),
              })
              .subscribe({
                next: (res) => {
                  console.log(`${isTeamsMeeting ? 'Teams' : 'Outlook'} meeting cancelled successfully.`, res)
                  this.loading = false
                  this.result = null 
                  resolve()
                },
                error: (err) => {
                  console.error(`Error cancelling ${isTeamsMeeting ? 'Teams' : 'Outlook'} meeting`, err)
                  this.loading = false
                  reject(err)
                },
              })
          },
          error: (err) => {
            console.error('Failed to acquire token for cancellation', err)
            this.loading = false
            reject(err)
          }
        })
    });
  }


    async signIn(): Promise<void> {
      if (this.loading) {
        return;
      }
  
      this.loading = true;
      await this.msal.instance.initialize()
  
      const existingAccount =
        this.msal.instance.getActiveAccount() || this.msal.instance.getAllAccounts()[0];
  
      if (existingAccount) {
        this.msal.instance.setActiveAccount(existingAccount);
        this.isSignedIn = true;
        this.loading = false;
        return;
      }
  
      this.msal
        .loginPopup({ scopes: ['User.Read', 'Calendars.ReadWrite'], prompt: 'select_account' })
        .subscribe({
          next: (response: AuthenticationResult) => {
            this.msal.instance.setActiveAccount(response.account);
            this.isSignedIn = true;
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        });
    }
}